import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { academicService } from '../services/api';
import { HorizontalBarChart } from './ui';

/** Course-wide stats: content counts, per-quiz pass rates, per-student
 * progress. Shared by the teacher's own course page and the admin course
 * list — the backend already allows either to call the same endpoint. */
export default function CourseStatsModal({ open, onClose, moduleId, moduleName }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !moduleId) return;
    let cancelled = false;
    setLoading(true);
    setStats(null);
    academicService.getModuleStatistics(moduleId)
      .then((res) => { if (!cancelled) setStats(res.data || res); })
      .catch(() => { if (!cancelled) setStats(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, moduleId]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] overflow-y-auto">
        <div className="p-5 border-b border-surface-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </span>
            <div>
              <h2 className="text-lg font-display font-bold text-gray-800">{t('Course Statistics')}</h2>
              <p className="text-gray-500 text-sm">{moduleName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:bg-surface-100 hover:text-gray-600 rounded-lg p-2 transition"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            </div>
          ) : stats ? (
            <div className="space-y-8">
              {/* Overview cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: t('Categories'), value: stats.categories_count },
                  { label: t('Lessons'), value: `${stats.published_lessons_count}/${stats.lessons_count}` },
                  { label: t('Quizzes'), value: stats.quizzes_count },
                  { label: t('Enrolled Students'), value: stats.enrolled_students_count },
                  { label: t('Average Student Progress'), value: `${stats.average_student_progress ?? 0}%` },
                  { label: t('Students Completed'), value: stats.completed_students_count ?? 0 },
                  { label: t('Average Quiz Score'), value: stats.average_quiz_score != null ? `${stats.average_quiz_score}%` : '—' },
                ].map((card) => (
                  <div key={card.label} className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-600 mb-1">{card.label}</p>
                    <p className="text-2xl font-bold text-gray-800">{card.value}</p>
                  </div>
                ))}
              </div>

              {/* Quiz performance */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">{t('Quiz Performance')}</h3>
                {stats.quizzes.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-4 py-2 font-medium text-gray-600">{t('Quiz')}</th>
                          <th className="text-left px-4 py-2 font-medium text-gray-600">{t('Lesson')}</th>
                          <th className="text-right px-4 py-2 font-medium text-gray-600">{t('Attempts')}</th>
                          <th className="text-right px-4 py-2 font-medium text-gray-600">{t('Students')}</th>
                          <th className="text-right px-4 py-2 font-medium text-gray-600">{t('Avg. Score')}</th>
                          <th className="text-right px-4 py-2 font-medium text-gray-600">{t('Pass Rate')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.quizzes.map((quiz) => (
                          <tr key={quiz.id} className="border-t border-gray-100">
                            <td className="px-4 py-2 text-gray-800 font-medium">{quiz.title}</td>
                            <td className="px-4 py-2 text-gray-600">{quiz.lesson_title}</td>
                            <td className="px-4 py-2 text-right text-gray-800">{quiz.attempts_count}</td>
                            <td className="px-4 py-2 text-right text-gray-800">{quiz.unique_students}</td>
                            <td className="px-4 py-2 text-right text-gray-800">{quiz.average_score != null ? `${quiz.average_score}%` : '—'}</td>
                            <td className="px-4 py-2 text-right">
                              {quiz.pass_rate != null ? (
                                <span className={`font-medium ${quiz.pass_rate >= 50 ? 'text-green-600' : 'text-red-600'}`}>{quiz.pass_rate}%</span>
                              ) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">{t('No quizzes yet')}</p>
                )}
              </div>

              {/* Student progress */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">{t('Student Progress')}</h3>
                {stats.students.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-4 py-2 font-medium text-gray-600">{t('Student')}</th>
                          <th className="text-right px-4 py-2 font-medium text-gray-600">{t('Lessons Completed')}</th>
                          <th className="text-left px-4 py-2 font-medium text-gray-600">{t('Progress')}</th>
                          <th className="text-right px-4 py-2 font-medium text-gray-600">{t('Quizzes Attempted')}</th>
                          <th className="text-right px-4 py-2 font-medium text-gray-600">{t('Avg. Quiz Score')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.students.map((student) => (
                          <tr key={student.id} className="border-t border-gray-100">
                            <td className="px-4 py-2 text-gray-800 font-medium">{student.name}</td>
                            <td className="px-4 py-2 text-right text-gray-800">{student.lessons_completed}/{student.lessons_total}</td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden max-w-[100px]">
                                  <div className="h-full bg-primary-500 rounded-full" style={{ width: `${student.progress_pct}%` }} />
                                </div>
                                <span className="text-xs text-gray-600 shrink-0">{student.progress_pct}%</span>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-right text-gray-800">{student.quizzes_attempted}/{student.quizzes_total}</td>
                            <td className="px-4 py-2 text-right text-gray-800">{student.average_quiz_score != null ? `${student.average_quiz_score}%` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">{t('No enrolled students yet')}</p>
                )}
              </div>

              {/* Graphs */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3">{t('Pass Rate by Quiz')}</h3>
                  <HorizontalBarChart
                    bars={stats.quizzes.map((q) => ({ label: q.title, value: q.pass_rate }))}
                    colorFor={(value) => (value == null ? '#d1d5db' : value >= 50 ? '#16a34a' : '#dc2626')}
                    emptyLabel={t('No quizzes yet')}
                  />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3">{t('Progress by Student')}</h3>
                  <HorizontalBarChart
                    bars={stats.students.map((s) => ({ label: s.name, value: s.progress_pct }))}
                    emptyLabel={t('No enrolled students yet')}
                  />
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">{t('Failed to load course statistics')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
